import { Search, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { LOYALTY_ENABLED, formatMoney } from '@sense/shared';
import { saveCustomer, searchCustomers, useCustomers, type CustomerRef } from '../lib/customers';
import { toast } from '../lib/toast';
import { Button, Field, Kbd, Sheet } from './ui';
import { tr } from '../lib/i18n';

/** Find a customer by name or mobile (offline), or add one in two fields. */
export function CustomerPicker({ open, onClose, onPick }: { open: boolean; onClose(): void; onPick(c: CustomerRef): void }) {
  const all = useCustomers() ?? [];
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  useEffect(() => { if (open) { setQ(''); setHi(0); setAdding(false); } }, [open]);
  const results = useMemo(() => searchCustomers(all, q), [all, q]);
  useEffect(() => setHi(0), [q]);

  const startAdd = () => {
    const digits = q.replace(/\D/g, '');
    setForm({ name: digits.length >= 6 ? '' : q.trim(), phone: digits.length >= 6 ? digits.slice(-10) : '' });
    setAdding(true);
  };
  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) return toast.error(tr('Enter a 10-digit mobile number.'));
    const c = await saveCustomer({ name: form.name, phone: form.phone });
    toast.ok(tr('{0} added', { 0: c.name }));
    onPick(c);
  };

  return (
    <Sheet open={open} onClose={onClose} title={adding ? tr('New customer') : tr('Customer')}>
      {adding ? (
        <form onSubmit={add} className="flex flex-col gap-3 pb-2">
          <Field label={tr('Name')} required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label={tr('Mobile number')} required inputMode="tel" maxLength={10} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '') })} />
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setAdding(false)}>{tr('Back')}</Button><Button>{tr('Add & use')}</Button></div>
        </form>
      ) : (
        <div className="flex flex-col gap-3 pb-2">
          <label className="flex h-12 items-center gap-2 rounded-full border-2 border-forest-600 bg-white px-4">
            <Search size={18} className="text-muted" aria-hidden />
            <span className="sr-only">{tr('Search customers')}</span>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr('Name or mobile number')} className="min-w-0 flex-1 bg-transparent outline-none"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
                else if (e.key === 'Enter') { e.preventDefault(); if (results[hi]) onPick(results[hi]); else startAdd(); }
              }} />
          </label>
          <ul className="flex flex-col gap-1" role="listbox" aria-label="Customers">
            {results.map((c, i) => (
              <li key={c.id} role="option" aria-selected={i === hi}>
                <button onMouseEnter={() => setHi(i)} onClick={() => onPick(c)} className={`flex w-full items-center gap-3 rounded-full px-2 py-2 text-left ${i === hi ? 'bg-forest-50' : ''}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-t-blue font-bold text-t-blue-fg">{c.name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()}</span>
                  <span className="min-w-0 flex-1"><span className="block truncate font-bold">{c.name}</span><span className="num text-xs text-muted">{c.phone}</span></span>
                  {c.balance > 0 && <span className="num rounded-full bg-t-red px-2.5 py-1 text-xs font-semibold text-danger">{tr('owes {0}', { 0: formatMoney(c.balance, { whole: true }) })}</span>}
                  {LOYALTY_ENABLED && c.points > 0 && <span className="num rounded-full bg-t-violet px-2.5 py-1 text-xs font-semibold text-t-violet-fg">{tr('{0} pts', { 0: c.points })}</span>}
                </button>
              </li>
            ))}
            {!results.length && <li className="px-2 py-4 text-center text-sm text-muted">{tr('No customer matches “{0}”.', { 0: q })}</li>}
          </ul>
          <Button variant="secondary" onClick={startAdd}><UserPlus size={18} />{tr('New customer')} {q && !results.length ? <Kbd>{tr('Enter')}</Kbd> : null}</Button>
        </div>
      )}
    </Sheet>
  );
}
